import React, { useState, useEffect, useMemo } from 'react';
import { Tv } from 'lucide-react';
import { RaceGroup, nachMonat } from '../components/ProRaceZeile';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ProRace } from '@radevents/shared';
import { buildRaceCalendarListSchema } from '../hooks/useProRaceSchema';
import { isRunning, isUpcoming } from '../utils/proRaceStatus';
import PublicHeader from '../components/Layout/PublicHeader';
import api from '../services/api';

import { SITE_ORIGIN } from '../config/siteOrigin';

const PAGE_TITLE = 'Profi-Rennkalender 2026 – Radsport live erleben';
const PAGE_DESC = "Alle wichtigen Profi-Radrennen 2026 auf einen Blick: Tour de France, Giro d'Italia, Vuelta a España und mehr – mit TV- und Streaming-Infos.";
const CANONICAL = `${SITE_ORIGIN}/rennkalender`;

type Reiter = 'kommend' | 'ergebnisse';

/**
 * Profi-Rennkalender (Neugestaltung 30.10.2026, Vorbild X/NBA-Spielplan und Sweatpals).
 *
 * Zwei Reiter statt einer langen Liste: **Kommend** (laufende Rennen oben unter „Läuft gerade“,
 * dann nach Monat) und **Ergebnisse** (jüngstes zuerst, mit Sieger in der Zeile). Vorher standen
 * ab Herbst nur noch blasse „Vergangen“-Karten auf der Seite. Ohne kommende Rennen öffnet die
 * Seite auf den Ergebnissen. Kategorien (WorldTour, ProSeries) filtern, sobald es mehr als eine gibt.
 */
export default function ProRaceCalendar() {
  const [races, setRaces] = useState<ProRace[]>([]);
  const [loading, setLoading] = useState(true);
  const [reiter, setReiter] = useState<Reiter | null>(null);
  const [kategorie, setKategorie] = useState<string | null>(null);

  useEffect(() => {
    api.get('/pro-races')
      .then(r => setRaces(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const kategorien = useMemo(
    () => Array.from(new Set(races.map(r => r.category).filter((c): c is string => !!c))).sort(),
    [races],
  );
  const gefiltert = kategorie ? races.filter(r => r.category === kategorie) : races;
  const running = gefiltert.filter(r => isRunning(r.startDate, r.endDate));
  const upcoming = gefiltert.filter(r => isUpcoming(r.endDate) && !isRunning(r.startDate, r.endDate))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const past = gefiltert.filter(r => !isUpcoming(r.endDate))
    .sort((a, b) => b.startDate.localeCompare(a.startDate));
  const offen = running.length + upcoming.length;
  // Erst der Klick entscheidet; bis dahin die Lage: ohne Kommendes zeigen wir die Ergebnisse.
  const aktiv: Reiter = reiter ?? (!loading && races.length > 0 && races.every(r => !isUpcoming(r.endDate)) ? 'ergebnisse' : 'kommend');

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESC} />
        {races.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify(buildRaceCalendarListSchema(races))}
          </script>
        )}
      </Helmet>

      <PublicHeader />

      <div className="rk-seite">
        <div className="rk-kopf">
          <div>
            <h1 className="rk-titel">Profi-Rennkalender 2026</h1>
            <p className="rk-unterzeile">UCI WorldTour und ProSeries, mit TV- und Streaming-Infos.</p>
          </div>
          <Link to="/radsport-im-tv" className="rk-tv"><Tv size={16} aria-hidden />Radsport im TV</Link>
        </div>

        <div className="rk-leiste">
          <div className="rk-reiter" role="tablist" aria-label="Ansicht">
            <button type="button" role="tab" aria-selected={aktiv === 'kommend'} className={aktiv === 'kommend' ? 'rk-pille rk-pille--aktiv' : 'rk-pille'} onClick={() => setReiter('kommend')}>
              Kommend{!loading && <span className="rk-zahl">{offen}</span>}
            </button>
            <button type="button" role="tab" aria-selected={aktiv === 'ergebnisse'} className={aktiv === 'ergebnisse' ? 'rk-pille rk-pille--aktiv' : 'rk-pille'} onClick={() => setReiter('ergebnisse')}>
              Ergebnisse{!loading && <span className="rk-zahl">{past.length}</span>}
            </button>
          </div>
          {kategorien.length > 1 && (
            <div className="rk-kategorien" role="group" aria-label="Kategorie">
              {kategorien.map(k => (
                <button key={k} type="button" aria-pressed={kategorie === k}
                  className={kategorie === k ? 'rk-kategorie rk-kategorie--aktiv' : 'rk-kategorie'}
                  onClick={() => setKategorie(kategorie === k ? null : k)}>
                  {k.replace(/^UCI\s+/, '')}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="rk-liste" aria-busy>{[0, 1, 2].map(i => <div key={i} className="rk-platzhalter" />)}</div>
        ) : aktiv === 'kommend' ? (
          offen === 0 ? (
            <div className="rk-leer">
              <p>Die Saison ist vorbei. Der Kalender für das nächste Jahr folgt, sobald die UCI ihn veröffentlicht.</p>
              {past.length > 0 && <button type="button" className="rk-leer-knopf" onClick={() => setReiter('ergebnisse')}>Ergebnisse ansehen</button>}
            </div>
          ) : (
            <>
              {running.length > 0 && <RaceGroup label="Läuft gerade" races={running} live />}
              {nachMonat(upcoming).map(g => <RaceGroup key={g.key} label={g.label} races={g.races} />)}
            </>
          )
        ) : past.length === 0 ? (
          <div className="rk-leer"><p>Noch keine Ergebnisse in dieser Saison.</p></div>
        ) : (
          nachMonat(past).map(g => <RaceGroup key={g.key} label={g.label} races={g.races} ergebnis />)
        )}
      </div>
    </div>
  );
}

export { RaceGroup };
